import { Component } from "@theme/component";
import { debounce } from "@theme/utilities";

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ["wrapper", "content", "marqueeItems"];

  #lastWidth = 0;
  #isTouching = false;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();
    this.#lastWidth = this.offsetWidth || this.refs.wrapper?.offsetWidth || 0;

    // Use ResizeObserver instead of window resize for more accurate tracking
    if (typeof ResizeObserver !== "undefined" && ResizeObserver) {
      this.#resizeObserver = new ResizeObserver(this.#handleResize);
      if (this.#resizeObserver) {
        this.#resizeObserver.observe(this);
      }
    } else {
      // Fallback to window resize
      window.addEventListener("resize", this.#handleResize);
    }

    // Prevent resize handling during touch interactions
    this.addEventListener("touchstart", this.#handleTouchStart, {
      passive: true,
    });
    this.addEventListener("touchend", this.#handleTouchEnd, { passive: true });
    this.addEventListener("touchcancel", this.#handleTouchEnd, {
      passive: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    } else {
      window.removeEventListener("resize", this.#handleResize);
    }

    this.removeEventListener("touchstart", this.#handleTouchStart);
    this.removeEventListener("touchend", this.#handleTouchEnd);
    this.removeEventListener("touchcancel", this.#handleTouchEnd);
  }

  #handleTouchStart = () => {
    this.#isTouching = true;
  };

  #handleTouchEnd = () => {
    // Delay clearing touch flag to avoid immediate resize handling
    setTimeout(() => {
      this.#isTouching = false;
    }, 100);
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty("--marquee-speed", `${value}s`);
  }

  #calculateSpeed() {
    const isMobile = window.innerWidth <= 749;

    const baseSpeed = Number(this.getAttribute("data-speed-factor")) || 25;
    const mobileSpeed = Number(this.getAttribute("data-speed-factor-mobile"));
    const speedFactor = Math.max(
      1,
      Number.isFinite(isMobile ? mobileSpeed : baseSpeed)
        ? isMobile && Number.isFinite(mobileSpeed) && mobileSpeed > 0
          ? mobileSpeed
          : baseSpeed
        : baseSpeed
    );

    const { marqueeItems } = this.refs;
    const marqueeWidth = Math.max(
      1,
      this.offsetWidth,
      this.refs.wrapper?.offsetWidth ?? 0
    );

    let marqueeRepeatedItemWidth =
      marqueeItems[0]?.offsetWidth ??
      marqueeItems[0]?.getBoundingClientRect?.().width ??
      marqueeItems[0]?.scrollWidth ??
      marqueeItems[0]?.clientWidth ??
      0;

    if (!marqueeRepeatedItemWidth || marqueeRepeatedItemWidth <= 0) {
      marqueeRepeatedItemWidth = 1;
    }

    const rawCount = marqueeWidth / marqueeRepeatedItemWidth;
    const normalizedCount = Math.max(
      1,
      Number.isFinite(rawCount) ? Math.ceil(rawCount) : 1
    );

    const speed = Math.sqrt(normalizedCount) * speedFactor;

    return Number.isFinite(speed) && speed > 0 ? speed : speedFactor;
  }

  #handleResize = debounce(() => {
    // Don't handle resize during touch interactions
    if (this.#isTouching) return;

    const currentWidth =
      this.offsetWidth || this.refs.wrapper?.offsetWidth || 0;

    // Only proceed if width actually changed significantly (more than 5px)
    if (Math.abs(currentWidth - this.#lastWidth) < 5) {
      return;
    }

    this.#lastWidth = currentWidth;

    const { marqueeItems } = this.refs;
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = marqueeItems.length;

    // Only update if copies actually need to change
    if (Math.abs(newNumberOfCopies - currentNumberOfCopies) > 0) {
      if (newNumberOfCopies > currentNumberOfCopies) {
        this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
      } else if (newNumberOfCopies < currentNumberOfCopies) {
        this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
      }

      this.#duplicateContent();
      this.#setSpeed();
      // Only restart animation if we actually changed something
      this.#restartAnimation();
    } else {
      // Just update speed if copies are the same but speed might have changed
      const oldSpeed = this.style.getPropertyValue("--marquee-speed");
      this.#setSpeed();
      const newSpeed = this.style.getPropertyValue("--marquee-speed");
      // Only restart if speed actually changed
      if (oldSpeed !== newSpeed) {
        this.#restartAnimation();
      }
    }
  }, 300);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    // Cancel existing animations to prevent glitches
    for (const animation of animations) {
      animation.cancel();
    }

    // Force reflow and restart animation
    requestAnimationFrame(() => {
      this.refs.wrapper.style.animation = "none";
      this.refs.wrapper.offsetHeight; // Force reflow
      this.refs.wrapper.style.animation = "";
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (
      this.refs.content.cloneNode(true)
    );

    clone.setAttribute("aria-hidden", "true");
    clone.removeAttribute("ref");

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { marqueeItems } = this.refs;
    const marqueeWidth = Math.max(
      1,
      this.offsetWidth,
      this.refs.wrapper?.offsetWidth ?? 0
    );

    let marqueeRepeatedItemWidth =
      marqueeItems[0]?.offsetWidth ??
      marqueeItems[0]?.getBoundingClientRect?.().width ??
      marqueeItems[0]?.scrollWidth ??
      marqueeItems[0]?.clientWidth ??
      0;

    if (!marqueeRepeatedItemWidth || marqueeRepeatedItemWidth <= 0) {
      marqueeRepeatedItemWidth = 1;
    }

    const rawCopies = marqueeWidth / marqueeRepeatedItemWidth;
    const normalizedCopies = Math.max(
      1,
      Number.isFinite(rawCopies) ? Math.ceil(rawCopies) : 1
    );

    // Add extra copies to ensure seamless loop
    return Math.max(normalizedCopies + 2, 3); // Ensure at least 3 copies for smooth animation
  }
}

if (!customElements.get("marquee-component")) {
  customElements.define("marquee-component", MarqueeComponent);
}
